import { Outlet } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useCurrentUser } from '../../hooks/useCurrentUser.jsx';
import {
  LayoutDashboard,
  PlusCircle,
  FileText,
  CheckSquare,
  Receipt,
  ReceiptText,
  History,
  CalendarDays,
  CalendarRange,
  Users,
  FolderOpen,
  Plane,
  Stamp,
  HelpCircle,
} from 'lucide-react';
import Sidebar, { useSidebar } from '../ui/Sidebar';
import { cn } from '@/lib/utils';
import WhatsNew from '../WhatsNew.jsx';

function EmployeeContent() {
  const sidebar = useSidebar();
  const collapsed = sidebar?.collapsed;

  return (
    <main
      className={cn(
        'min-h-screen transition-all duration-200 bg-background',
        'pt-16 lg:pt-0',
        collapsed ? 'lg:ml-[60px]' : 'lg:ml-[240px]'
      )}
    >
      {/* Content width scales up on bigger monitors. Pages that should stay
          narrow (forms, etc.) set their own inner max-width, so this only
          widens the width-hungry pages (expense approval, dashboard, lists). */}
      <div className="mx-auto max-w-5xl xl:max-w-6xl 2xl:max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
        <Outlet />
      </div>
    </main>
  );
}

export default function EmployeeLayout() {
  const { accounts, instance } = useMsal();
  const account = accounts[0];
  const { user, isAdmin, isApprover, isFinance, sections } = useCurrentUser();
  // Finance is an approver for EXPENSE chains only — they should not
  // see Leave Approvals or Team Overview (which is leave-oriented).
  // Admins and traditional approvers see everything.
  const isLeaveApprover = user?.role === 'admin' || user?.role === 'approver';
  // The workspace switcher offers the Admin Panel link. Anyone who can render
  // at least one admin page — a true admin, finance, or a custom role granted
  // any section — should see it so they can flip to the admin view.
  const canSeeAdminPanel = isAdmin || isFinance || (Array.isArray(sections) && sections.length > 0);

  const navSections = [
    {
      label: null,
      items: [
        { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true, 'data-tour': 'emp-dashboard' },
        { to: '/team-calendar', label: 'Team Calendar', icon: CalendarDays, 'data-tour': 'emp-team-calendar' },
        ...(isLeaveApprover ? [{ to: '/team-overview', label: 'Team Overview', icon: Users }] : []),
      ],
    },
    {
      label: 'Leave',
      items: [
        { to: '/planner', label: 'Planner', icon: CalendarRange, 'data-tour': 'emp-planner' },
        { to: '/my-requests', label: 'My Requests', icon: FileText, 'data-tour': 'emp-requests' },
      ],
    },
    {
      label: 'Expenses',
      items: [
        { to: '/expenses', label: 'My Expenses', icon: Receipt, 'data-tour': 'emp-expenses' },
        { to: '/expenses/new', label: 'New Expense', icon: PlusCircle },
      ],
    },
    {
      label: 'Travel',
      items: [
        { to: '/travel', label: 'My Travel', icon: Plane, 'data-tour': 'emp-travel' },
        { to: '/my-visas', label: 'My Visas', icon: Stamp },
      ],
    },
    {
      label: 'Documents',
      items: [
        { to: '/my-contract', label: 'My Contract', icon: FileText, 'data-tour': 'emp-documents' },
        { to: '/my-documents', label: 'My Documents', icon: FolderOpen },
      ],
    },
    {
      label: null,
      items: [
        { to: '/help', label: 'Help', icon: HelpCircle, 'data-tour': 'emp-help' },
      ],
    },
  ];

  if (isApprover) {
    const approvalItems = [];
    if (isLeaveApprover) {
      approvalItems.push({ to: '/approvals', label: 'Leave Approvals', icon: CheckSquare });
    }
    approvalItems.push({ to: '/expense-approvals', label: 'Expense Approvals', icon: ReceiptText, end: true });
    approvalItems.push({ to: '/expense-approvals/history', label: 'Approval History', icon: History });
    approvalItems.push({ to: '/visa-approvals', label: 'Visa Approvals', icon: Plane });
    navSections.push({ label: 'Approvals', items: approvalItems });
  }

  const displayName = user?.displayName || account?.name || 'User';

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        navSections={navSections}
        brandName="KSA ProHR"
        storageKey="employee-sidebar-collapsed"
        showWorkspaceSwitcher={canSeeAdminPanel}
        isAdmin={canSeeAdminPanel}
        aboveUserSlot={({ collapsed }) => <WhatsNew collapsed={collapsed} />}
        userInfo={{
          name: displayName,
          email: user?.email || account?.username,
          initials: displayName.split(' ').map(n => n[0]).join('').slice(0, 2),
        }}
        onLogout={() => instance.logoutRedirect()}
      />
      <EmployeeContent />
    </div>
  );
}
